import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup';
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      if (mode === 'signin') {
        const { error } = await signIn(emailOrUsername, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess('Successfully signed in!');
          setTimeout(() => onClose(), 1500);
        }
      } else {
        const { error } = await signUp(emailOrUsername, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess('Successfully signed up! Please check your email to confirm your account.');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmailOrUsername('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    resetForm();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border-2 border-gray-700 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/user.svg" 
              alt="User" 
              className="w-6 h-6"
              style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
            />
            {mode === 'signin' ? 'Sign In' : 'Sign Up'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg" 
              alt="Close" 
              className="w-6 h-6"
              style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
            />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border-2 border-dashed border-red-600 rounded-lg text-red-200 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-900/30 border-2 border-dashed border-green-600 rounded-lg text-green-200 text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {mode === 'signin' ? 'Email or Username' : 'Email'}
            </label>
            <input
              type={mode === 'signin' ? 'text' : 'email'}
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              required
              disabled={loading}
              className="w-full p-3 bg-gray-800 border-2 border-gray-700 rounded-md text-white focus:border-blue-500 focus:outline-none transition-colors disabled:opacity-50"
              placeholder={mode === 'signin' ? 'Enter your email or username' : 'Enter your email'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              disabled={loading}
              className="w-full p-3 bg-gray-800 border-2 border-gray-700 rounded-md text-white focus:border-blue-500 focus:outline-none transition-colors disabled:opacity-50"
              placeholder="Enter your password"
            />
          </div>

          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
                className="w-full p-3 bg-gray-800 border-2 border-gray-700 rounded-md text-white focus:border-blue-500 focus:outline-none transition-colors disabled:opacity-50"
                placeholder="Confirm your password"
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 p-3 bg-gray-700 text-white hover:bg-gray-600 transition-colors rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 p-3 bg-blue-600 text-white font-medium transition-all duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-md"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {mode === 'signin' ? 'Signing In...' : 'Signing Up...'}
                </>
              ) : (
                <>
                  <img 
                    src={`https://unpkg.com/pixelarticons@1.8.0/svg/${mode === 'signin' ? 'login' : 'user-plus'}.svg`}
                    alt={mode === 'signin' ? 'Sign In' : 'Sign Up'} 
                    className="w-4 h-4"
                    style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                  />
                  {mode === 'signin' ? 'Sign In' : 'Sign Up'}
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}
            <button
              onClick={switchMode}
              disabled={loading}
              className="ml-2 text-blue-400 hover:text-blue-300 transition-colors font-medium disabled:opacity-50"
            >
              {mode === 'signin' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}; 